import { createContext, useContext, useState, useEffect, useCallback } from "react";

const MsalContext = createContext(null);

export function MsalProvider({ children }) {
  const [msalConfig, setMsalConfig] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load config from localStorage on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem("ms365_config");
    const savedToken = localStorage.getItem("ms365_token");
    const savedUser = localStorage.getItem("ms365_user");
    const tokenExpiry = localStorage.getItem("ms365_token_expiry");
    
    if (savedConfig) {
      setMsalConfig(JSON.parse(savedConfig));
    }
    
    if (savedToken && tokenExpiry) {
      const expiry = new Date(tokenExpiry);
      if (expiry > new Date()) {
        setAccessToken(savedToken);
        setIsAuthenticated(true);
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
      } else {
        // Token expired, clear it
        localStorage.removeItem("ms365_token");
        localStorage.removeItem("ms365_token_expiry");
        localStorage.removeItem("ms365_user");
      }
    }
    
    setIsLoading(false);
  }, []);

  const saveConfig = useCallback((config) => {
    setMsalConfig(config);
    localStorage.setItem("ms365_config", JSON.stringify(config));
  }, []);

  const clearConfig = useCallback(() => {
    setMsalConfig(null);
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("ms365_config");
    localStorage.removeItem("ms365_token");
    localStorage.removeItem("ms365_token_expiry");
    localStorage.removeItem("ms365_user");
  }, []);

  const getAuthUrl = useCallback(() => {
    if (!msalConfig) return null;
    
    const { clientId, tenantId } = msalConfig;
    // Use production URL for redirect
    const productionUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '').replace('https://', 'https://') || window.location.origin;
    const baseUrl = false 
      ? productionUrl.split('/api')[0] 
      : window.location.origin;
    const redirectUri = `${baseUrl}/auth/callback`;
    const scope = encodeURIComponent("User.Read Files.Read Sites.Read.All offline_access");
    const state = encodeURIComponent(JSON.stringify({ source: "ms365_auth" }));
    
    return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=token&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}&response_mode=fragment`;
  }, [msalConfig]);

  const handleAuthCallback = useCallback((hash) => {
    try {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get("access_token");
      const expiresIn = params.get("expires_in");
      
      if (token) {
        setAccessToken(token);
        setIsAuthenticated(true);
        localStorage.setItem("ms365_token", token);
        
        // Calculate expiry
        const expiry = new Date();
        expiry.setSeconds(expiry.getSeconds() + parseInt(expiresIn || "3600"));
        localStorage.setItem("ms365_token_expiry", expiry.toISOString());
        
        // Fetch user info
        fetchUserInfo(token);
        
        return { success: true };
      }
      
      const error = params.get("error");
      const errorDescription = params.get("error_description");
      if (error) {
        return { success: false, error: errorDescription || error };
      }
      
      return { success: false, error: "No token received" };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, []);

  const fetchUserInfo = async (token) => {
    try {
      const response = await fetch("https://graph.microsoft.com/v1.0/me", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem("ms365_user", JSON.stringify(userData));
      }
    } catch (e) {
      console.error("Failed to fetch user info:", e);
    }
  };

  const logout = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("ms365_token");
    localStorage.removeItem("ms365_token_expiry");
    localStorage.removeItem("ms365_user");
  }, []);

  const value = {
    msalConfig,
    saveConfig,
    clearConfig,
    accessToken,
    user,
    isAuthenticated,
    isLoading,
    getAuthUrl,
    handleAuthCallback,
    logout
  };

  return (
    <MsalContext.Provider value={value}>
      {children}
    </MsalContext.Provider>
  );
}

export function useMsal() {
  const context = useContext(MsalContext);
  if (!context) {
    throw new Error("useMsal must be used within a MsalProvider");
  }
  return context;
}
